import React, { useEffect, useMemo, useRef, useState } from "react";
import { Image as ImageIcon, X, ChevronDown } from "lucide-react";
import { createHttpClient } from "../../api/client";
import config from "../../config/env";
import { ReactComponent as Plus } from "../../assets/images/plus.svg";
import { useSessionContext } from "../../contexts/session-context";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function UnderlineInput({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled = false,
}) {
  return (
    <div className="py-3">
      <div className={cx("text-sm", disabled ? "text-slate-400" : "text-slate-600")}>
        {label}
      </div>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className={cx(
          "mt-1 w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]",
          "border-b",
          disabled
            ? "text-slate-400 border-[#00000014]"
            : "text-slate-900 border-[#00000026]"
        )}
      />
    </div>
  );
}

function joinImgUrl(base, path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return b && p ? `${b}/${p}` : "";
}

/* -------------------- Modals -------------------- */
function ModalOverlay({ open, children, onBackdrop }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onBackdrop?.();
      }}
    >
      {children}
    </div>
  );
}

function LoadingModal({ open, text = "กำลังดำเนินการ..." }) {
  return (
    <ModalOverlay open={open}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-slate-900 animate-spin" />
          <div>
            <div className="text-base font-bold text-slate-900">{text}</div>
            <div className="text-sm text-slate-500">กรุณารอสักครู่</div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

function NoticeModal({ open, type = "success", title, message, onClose }) {
  if (!open) return null;
  const isSuccess = type === "success";

  return (
    <ModalOverlay open={open} onBackdrop={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div
            className={cx(
              "h-10 w-10 rounded-full flex items-center justify-center",
              isSuccess ? "bg-emerald-50" : "bg-rose-50"
            )}
          >
            <div
              className={cx(
                "h-3 w-3 rounded-full",
                isSuccess ? "bg-emerald-600" : "bg-rose-600"
              )}
            />
          </div>

          <div className="flex-1">
            <div className="text-lg font-extrabold text-slate-900">
              {title || (isSuccess ? "บันทึกข้อมูลสำเร็จ" : "เกิดข้อผิดพลาด")}
            </div>
            {message ? (
              <div className="mt-1 text-sm text-slate-600 whitespace-pre-line">
                {message}
              </div>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className={cx(
            "mt-5 w-full h-11 rounded-xl font-bold shadow-md active:scale-[0.99] hover:opacity-95",
            isSuccess ? "bg-slate-900 text-white" : "bg-rose-600 text-white"
          )}
        >
          ปิด
        </button>
      </div>
    </ModalOverlay>
  );
}

function AddAccountModal({ open, onClose, onSubmit, submitting }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [localErr, setLocalErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setUsername("");
    setPassword("");
    setLocalErr("");
  }, [open]);

  if (!open) return null;

  const canSubmit = String(username).trim() && String(password).trim() && !submitting;

  return (
    <ModalOverlay open={open} onBackdrop={submitting ? undefined : onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
          <div className="text-lg font-extrabold text-slate-900">เพิ่มบัญชี</div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-9 w-9 rounded-full hover:bg-black/5 flex items-center justify-center disabled:opacity-60"
            aria-label="close"
          >
            <X className="h-5 w-5 text-slate-700" />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="space-y-5">
            <div>
              <div className="text-sm text-slate-600 font-semibold">Username</div>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={submitting}
                placeholder="กรอกข้อมูล"
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300 disabled:opacity-60"
              />
            </div>

            <div>
              <div className="text-sm text-slate-600 font-semibold">Password</div>
              <div className="relative mt-2">
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  placeholder="กรอกข้อมูล"
                  type="password"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm outline-none focus:border-blue-300 disabled:opacity-60"
                />
                {/* <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                  <ChevronDown size={18} />
                </span> */}
              </div>
            </div>

            {localErr ? (
              <div className="text-sm text-rose-600">{localErr}</div>
            ) : null}
          </div>
        </div>

        <div className="px-6 pb-6 flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50 disabled:opacity-60"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              const u = String(username).trim();
              const p = String(password).trim();
              if (!u || !p) {
                setLocalErr("กรุณากรอก Username และ Password");
                return;
              }
              setLocalErr("");
              onSubmit?.({ username: u, password: p });
            }}
            className={cx(
              "h-11 flex-1 rounded-xl text-sm font-bold shadow-md active:scale-[0.99] hover:opacity-95",
              canSubmit ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed"
            )}
          >
            {submitting ? "กำลังเพิ่ม..." : "เพิ่ม"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

/* -------------------- Page -------------------- */
export default function ProfilePage() {
  const http = useMemo(() => createHttpClient(), []);
  const fileRef = useRef(null);

  const [initialEmail, setInitialEmail] = useState("");

  const [logoUrl, setLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); // ยังไม่ส่งตอนนี้

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const [notice, setNotice] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const [session] = useSessionContext();
  const currentRoleName = String(
    session?.roleName || localStorage.getItem("auth_role_name") || ""
  )
    .trim()
    .toLowerCase();
  const isSuperAdmin = currentRoleName === "superadmin";

  const closeNotice = () => setNotice((s) => ({ ...s, open: false }));

  // ✅ baseline หลังโหลดเสร็จ
  const baselineRef = useRef(null);

  const hasLogo = !!logoUrl;

  const revokeIfBlob = (url) => {
    if (url && String(url).startsWith("blob:")) {
      try {
        URL.revokeObjectURL(url);
      } catch { }
    }
  };

  // ✅ add account modal
  const [openAdd, setOpenAdd] = useState(false);
  const [adding, setAdding] = useState(false);

  // ✅ GET /auth/profile
  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await http.get("/auth/profile");
        const u = res?.data?.user;

        if (!u?.id) throw new Error("No user found from /auth/profile");
        if (!alive) return;

        const imgUrl = joinImgUrl(config.apiImg, u?.profile_image?.url || "");

        setUsername(u.username ?? "");
        setEmail(u.email ?? "");
        setInitialEmail(u.email ?? "");

        setLogoUrl(imgUrl);
        setLogoFile(null);
        setLogoRemoved(false);

        setPassword("");

        baselineRef.current = {
          username: String(u.username ?? "").trim(),
          email: String(u.email ?? "").trim(),
          logoUrl: imgUrl || "",
        };
      } catch (e) {
        console.error("GET /auth/profile error:", e);
        if (!alive) return;

        setError("โหลดข้อมูลผู้ใช้ไม่สำเร็จ");
        setNotice({
          open: true,
          type: "error",
          title: "โหลดข้อมูลไม่สำเร็จ",
          message: "ไม่สามารถดึงข้อมูลโปรไฟล์ได้ กรุณาลองใหม่อีกครั้ง",
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [http]);

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    revokeIfBlob(logoUrl);
    const url = URL.createObjectURL(f);

    setLogoUrl(url);
    setLogoFile(f);
    setLogoRemoved(false);

    if (fileRef.current) fileRef.current.value = "";
  };

  const onRemove = () => {
    revokeIfBlob(logoUrl);

    setLogoUrl("");
    setLogoFile(null);
    setLogoRemoved(true);

    if (fileRef.current) fileRef.current.value = "";
  };

  const isChanged = useMemo(() => {
    const base = baselineRef.current;
    if (!base) return false;

    const usernameChanged = (username ?? "").trim() !== (base.username ?? "");
    const emailChanged = (email ?? "").trim() !== (base.email ?? "");
    const logoChanged = !!logoFile || logoRemoved || (logoUrl ?? "") !== (base.logoUrl ?? "");

    return usernameChanged || emailChanged || logoChanged;
  }, [username, email, logoFile, logoRemoved, logoUrl]);

  const isSaveDisabled = loading || saving || !baselineRef.current || !isChanged;

  // ✅ PUT /auth/profile (multipart/form-data)
  const onSave = async () => {
    if (isSaveDisabled) return;

    setSaving(true);
    setError("");

    try {
      const base = baselineRef.current || {};

      const finalUsername = (username ?? "").trim() || base.username || "";
      const finalEmail = (email ?? "").trim() || base.email || initialEmail || "";

      const formData = new FormData();
      formData.append("username", finalUsername);
      formData.append("email", finalEmail);

      if (logoFile) {
        formData.append("files.profile_image", logoFile);
      }

      // ลบรูป
      if (logoRemoved && !logoFile) {
        formData.append("profile_image", "");
      }

      const res = await http.put("/auth/profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updatedUser = res?.data?.user;

      const updatedImgUrl = joinImgUrl(
        config.apiImg,
        updatedUser?.profile_image?.url || (logoRemoved ? "" : "")
      );

      baselineRef.current = {
        username: String(updatedUser?.username ?? finalUsername).trim(),
        email: String(updatedUser?.email ?? finalEmail).trim(),
        logoUrl: updatedImgUrl || "",
      };

      setUsername(baselineRef.current.username);
      setEmail(baselineRef.current.email);

      revokeIfBlob(logoUrl);
      setLogoUrl(baselineRef.current.logoUrl);

      setLogoFile(null);
      setLogoRemoved(false);
      setPassword("");

      setNotice({
        open: true,
        type: "success",
        title: "บันทึกข้อมูลสำเร็จ",
        message: "อัปเดตโปรไฟล์เรียบร้อยแล้ว",
      });
    } catch (e) {
      console.error("PUT /auth/profile error:", e);
      setError("บันทึกข้อมูลไม่สำเร็จ");

      setNotice({
        open: true,
        type: "error",
        title: "บันทึกข้อมูลไม่สำเร็จ",
        message:
          e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          "กรุณาลองใหม่อีกครั้ง",
      });
    } finally {
      setSaving(false);
    }
  };

  // ✅ POST /auth/local/register
  const onAddAccount = async ({ username, password }) => {
    setAdding(true);
    try {
      await http.post("/auth/local/register", { username, password });
      setOpenAdd(false);
      setNotice({
        open: true,
        type: "success",
        title: "เพิ่มบัญชีใหม่สำเร็จ",
        message: "สร้างบัญชีเรียบร้อยแล้ว",
      });
    } catch (e) {
      console.error("POST /auth/local/register error:", e);
      setNotice({
        open: true,
        type: "error",
        title: "เพิ่มบัญชีไม่สำเร็จ",
        message:
          e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "กรุณาลองใหม่อีกครั้ง",
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="p-5 md:p-8 bg-white min-h-full">
      {/* Modals */}
      <LoadingModal
        open={loading || saving}
        text={saving ? "กำลังบันทึกข้อมูล..." : "กำลังโหลดข้อมูล..."}
      />
      <NoticeModal
        open={notice.open}
        type={notice.type}
        title={notice.title}
        message={notice.message}
        onClose={closeNotice}
      />
      <AddAccountModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSubmit={onAddAccount}
        submitting={adding}
      />

      <div>
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <span>ตั้งค่า</span>
        </div>
        <div className="mt-1 text-4xl font-extrabold tracking-tight">โปรไฟล์</div>
      </div>

      {error ? (
        <div className="mt-4 max-w-5xl rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="mt-8 max-w-5xl">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xl font-extrabold text-slate-900"></div>

          <div className="flex items-center gap-3">


            {/* บันทึก */}
            <button
              type="button"
              onClick={onSave}
              disabled={isSaveDisabled}
              className={cx(
                "h-11 px-8 rounded-xl font-bold shadow-md active:scale-[0.99] transition",
                isSaveDisabled
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-[#111111] text-white hover:opacity-95"
              )}
            >
              {saving ? "กำลังบันทึก..." : "บันทึก"}
            </button>
          </div>
        </div>

        <div className="mt-8 max-w-3xl">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-500">
              กำลังโหลดข้อมูล...
            </div>
          ) : (
            <>
              <UnderlineInput
                label="Username"
                value={username}
                onChange={setUsername}
                placeholder="กรอกชื่อผู้ใช้"
                disabled={saving}
              />

              {/* ถ้าจะเปิดแก้ email ให้เอาคอมเมนต์ออก */}
              {/* <UnderlineInput
                label="Email"
                value={email}
                onChange={setEmail}
                placeholder="กรอกอีเมล"
                type="email"
                disabled={saving}
              /> */}

              <UnderlineInput
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="******"
                type="password"
                disabled={saving}
              />
            </>
          )}

          {isSuperAdmin &&
            <button
              type="button"
              onClick={() => setOpenAdd(true)}
              disabled={loading || saving || adding}
              className={cx(
                "h-11 px-6 rounded-xl font-bold shadow-md active:scale-[0.99] transition",
                loading || saving || adding
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-[#1D1616] hover:bg-[#4C4444] text-white hover:opacity-95"
              )}
            >
              <Plus className="inline-block h-6 w-6 mr-2" />
              เพิ่มบัญชี
            </button>
          }
        </div>
      </div>
    </div>
  );
}